/** Shared helpers for public storefront pages */

import { initShopTracking } from './shop_tracking';

export const PLACEHOLDER_IMAGES = {
  product: 'https://images.unsplash.com/photo-1523275335684-37898b6baf30?w=400&h=400&fit=crop&q=80',
  digital: 'https://images.unsplash.com/photo-1516321318423-f06f85e504b3?w=400&h=400&fit=crop&q=80',
  course: 'https://images.unsplash.com/photo-1516321497487-e288fb19713f?w=400&h=400&fit=crop&q=80',
  donation: 'https://images.unsplash.com/photo-1532629345422-751c0ecd714b?w=400&h=400&fit=crop&q=80',
};

export const DEFAULT_THEME = {
  theme_color: '#1976d2',
  theme_accent_color: '',
  theme_preset: '',
  theme_header_style: 'gradient',
  theme_button_style: 'rounded',
};

export const THEME_PRESETS = [
  { id: 'blue_ocean', name: 'Blue Ocean', emoji: '🌊', primary: '#1976d2', accent: '#42a5f5' },
  { id: 'green_fresh', name: 'Green Fresh', emoji: '🌿', primary: '#2e7d32', accent: '#66bb6a' },
  { id: 'purple_modern', name: 'Purple Modern', emoji: '💜', primary: '#7b1fa2', accent: '#ba68c8' },
  { id: 'orange_warm', name: 'Orange Warm', emoji: '🔥', primary: '#e65100', accent: '#ff9800' },
  { id: 'dark_elegant', name: 'Dark Elegant', emoji: '🖤', primary: '#263238', accent: '#546e7a' },
  { id: 'pink_boutique', name: 'Pink Boutique', emoji: '🌸', primary: '#c2185b', accent: '#f06292' },
  { id: 'teal_calm', name: 'Teal Calm', emoji: '🧘', primary: '#00695c', accent: '#4db6ac' },
  { id: 'indigo_pro', name: 'Indigo Pro', emoji: '💼', primary: '#283593', accent: '#5c6bc0' },
];

export function formatRupiah(v) {
  return 'Rp ' + (v || 0).toLocaleString('id-ID');
}

export function normalizeHex(color, fallback = '#1976d2') {
  if (!color || typeof color !== 'string') return fallback;
  let hex = color.trim();
  if (!hex.startsWith('#')) hex = `#${hex}`;
  if (/^#[0-9a-fA-F]{3}$/.test(hex)) {
    hex = `#${hex[1]}${hex[1]}${hex[2]}${hex[2]}${hex[3]}${hex[3]}`;
  }
  if (!/^#[0-9a-fA-F]{6}$/.test(hex)) return fallback;
  return hex.toLowerCase();
}

function hexToRgb(hex) {
  const c = normalizeHex(hex);
  return {
    r: parseInt(c.slice(1, 3), 16),
    g: parseInt(c.slice(3, 5), 16),
    b: parseInt(c.slice(5, 7), 16),
  };
}

export function darkenColor(hex, amount = 0.15) {
  const { r, g, b } = hexToRgb(hex);
  const f = 1 - amount;
  const to = (v) => Math.max(0, Math.min(255, Math.round(v * f)));
  return `#${[to(r), to(g), to(b)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

export function lightenColor(hex, amount = 0.2) {
  const { r, g, b } = hexToRgb(hex);
  const to = (v) => Math.max(0, Math.min(255, Math.round(v + (255 - v) * amount)));
  return `#${[to(r), to(g), to(b)].map(v => v.toString(16).padStart(2, '0')).join('')}`;
}

export function isColorDark(hex) {
  const { r, g, b } = hexToRgb(hex);
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
  return luminance < 0.55;
}

export function getContrastText(hex) {
  return isColorDark(hex) ? '#ffffff' : '#1a1a2e';
}

export function themeGradient(color, fallback = '#1976d2') {
  const c = normalizeHex(color, normalizeHex(fallback));
  return `linear-gradient(135deg, ${c} 0%, ${c}dd 100%)`;
}

export function themeSolid(color, fallback = '#1976d2') {
  return normalizeHex(color, normalizeHex(fallback));
}

export function normalizeShopTheme(shop) {
  const src = shop || {};
  return {
    theme_color: normalizeHex(src.theme_color, DEFAULT_THEME.theme_color),
    theme_accent_color: src.theme_accent_color ? normalizeHex(src.theme_accent_color) : '',
    theme_preset: src.theme_preset || '',
    theme_header_style: src.theme_header_style === 'solid' ? 'solid' : 'gradient',
    theme_button_style: src.theme_button_style === 'pill' ? 'pill' : 'rounded',
  };
}

/** Merge theme fields from multiple partial shop/settings objects (last wins). */
export function pickShopTheme(...sources) {
  const merged = {};
  const keys = ['theme_color', 'theme_accent_color', 'theme_preset', 'theme_header_style', 'theme_button_style'];
  sources.forEach((src) => {
    if (!src) return;
    keys.forEach((k) => {
      if (src[k] != null && src[k] !== '') merged[k] = src[k];
    });
  });
  return merged;
}

export function buildShopThemeVars(shop) {
  const t = normalizeShopTheme(shop);
  const primary = t.theme_color;
  const accent = t.theme_accent_color || lightenColor(primary, 0.22);
  const headerStyle = t.theme_header_style;
  const buttonStyle = t.theme_button_style;
  return {
    primary,
    accent,
    primaryDark: darkenColor(primary, 0.18),
    headerText: getContrastText(primary),
    headerBackground: headerStyle === 'solid' ? primary : themeGradient(primary),
    btnRadius: buttonStyle === 'pill' ? '50px' : '8px',
    headerStyle,
    buttonStyle,
    preset: t.theme_preset,
  };
}

export function applyShopTheme(shop) {
  const vars = buildShopThemeVars(shop);
  const root = document.documentElement;
  root.style.setProperty('--shop-primary', vars.primary);
  root.style.setProperty('--shop-primary-dark', vars.primaryDark);
  root.style.setProperty('--shop-accent', vars.accent);
  root.style.setProperty('--shop-header-text', vars.headerText);
  root.style.setProperty('--shop-btn-radius', vars.btnRadius);
  root.style.setProperty('--theme-color', vars.primary);
  if (shop) initShopTracking(shop);
  return vars;
}

export function shopHeaderBackground(shop) {
  return buildShopThemeVars(shop).headerBackground;
}

export function productImage(url, type) {
  if (url && String(url).trim()) return url;
  return type === 'digital' ? PLACEHOLDER_IMAGES.digital : PLACEHOLDER_IMAGES.product;
}

export function courseImage(url) {
  if (url && String(url).trim()) return url;
  return PLACEHOLDER_IMAGES.course;
}

export function donationImage(url) {
  if (url && String(url).trim()) return url;
  return PLACEHOLDER_IMAGES.donation;
}

export function groupPaymentChannels(channels) {
  const groups = { manual: [], qris: [], va: [], ewallet: [], convenience: [] };
  (channels || []).forEach(ch => {
    if (groups[ch.group]) groups[ch.group].push(ch);
  });
  return groups;
}

export function formatPaymentFee(ch) {
  const parts = [];
  if (ch.fee_flat) parts.push('Rp ' + ch.fee_flat.toLocaleString('id-ID'));
  if (ch.fee_percent) parts.push(ch.fee_percent + '%');
  return parts.join(' + ');
}

export async function copyToClipboard(text, toast) {
  try {
    await navigator.clipboard.writeText(text);
    if (toast) toast.success('Disalin ke clipboard');
    return true;
  } catch (e) {
    if (toast) toast.error('Gagal menyalin');
    return false;
  }
}

export const shopPublicMixin = {
  methods: {
    formatRupiah,
    copyText(text) {
      return copyToClipboard(text, this.$toast);
    },
    applyShopThemeFromData(data) {
      if (data) applyShopTheme(data);
    },
  },
};
